import { staffService } from '@/modules/staff/services/staffService';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import storageService, { STORAGE_KEYS } from '@/modules/common/services/storageService';
import staffPermissionsStore from './staffPermissionsStore';
import staffSlotsStore from './staffSlotsStore';

const MAX_VISIBLE_QUICK_ACTIONS = 6;

const getMappedQuickActions = (quickActions) => quickActions
  .sort((a, b) => a.order - b.order)
  .map((value) => ({
    ...value,
    // eslint-disable-next-line global-require,import/no-dynamic-require
    image: require(`@/assets/images/quick-actions/${value.name}.svg`),
    translationKey: `common.quick_actions_menu.${value.name}`,
  }));

const state = {
  permissions: [],
  staffData: {},
  staffDataLoaded: false,
  staffMemberDataLoaded: false,
  staffList: [],
  allStaffLoaded: false,
  staffTeamsViewState: 'all',
  quickActions: [],
  staffMemberData: {},
};

const mutations = {
  setStaffData(state, staffData) {
    state.staffData = { ...state.staffData, [staffData.id]: { ...state.staffData[staffData.id], ...staffData } };
    storageService.save(`${STORAGE_KEYS.defaultPage}_${staffData.id}`, staffData.on_entry_navigate_to);
    state.staffDataLoaded = true;
  },
  setStaffMemberData(state, staffData) {
    state.staffMemberData = staffData;
    storageService.save(`${STORAGE_KEYS.defaultPage}_${staffData.uid}`, staffData.default_homepage);
    state.staffMemberDataLoaded = true;
  },
  setPermissions(state, permissions) {
    state.permissions = permissions;
  },
  setAllStaff(state, staff) {
    state.staffList = staff;
    const staffObj = staff.reduce((a, v) => ({ ...a, [v.uid]: v }), {});
    const mergedStaffData = {};

    Object.keys(staffObj).forEach((uid) => {
      if (state.staffData[uid]) {
        mergedStaffData[uid] = { ...staffObj[uid], ...state.staffData[uid] };
      } else {
        mergedStaffData[uid] = staffObj[uid];
      }
    });

    state.staffData = { ...state.staffData, ...mergedStaffData };
    state.allStaffLoaded = true;
  },
  setStaffTeamsView(state, teamViewState) {
    state.staffTeamsViewState = teamViewState;
  },
  setStaffDefaultPage(state, { staffUid, defaultPage }) {
    if (state.staffData[staffUid]) {
      state.staffData = {
        ...state.staffData,
        [staffUid]: {
          ...state.staffData[staffUid],
          on_entry_navigate_to: defaultPage,
        },
      };
    }
    storageService.save(`${STORAGE_KEYS.defaultPage}_${staffUid}`, defaultPage);
  },
  setQuickActions(state, quickActions) {
    state.quickActions = quickActions;
  },
};

const actions = {
  async fetchStaffData({ rootGetters, commit, state }, payload) {
    if (state.staffDataLoaded && !payload?.forceReload) return;
    try {
      const staffUid = rootGetters['AuthStore/staffUid'];
      const businessUid = rootGetters['AuthStore/businessUid'];
      const staffData = await staffService.fetchStaffData(staffUid, businessUid);
      commit('setStaffData', staffData);
    } catch (e) {
      console.error('error', e);
    }
  },
  async fetchPermissions({ rootGetters, commit, state }) {
    if (state.permissions.length) return;
    try {
      const staffUid = rootGetters['AuthStore/staffUid'];
      const permissions = await staffService.fetchPermissions(staffUid);
      commit('setPermissions', permissions);
    } catch (e) {
      console.error('error', e);
    }
  },
  async fetchStaffDataByUid({ rootGetters, commit }, staffUid) {
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      const staffData = await staffService.fetchStaffData(staffUid, businessUid);
      commit('setStaffData', staffData);
      return staffData;
    } catch (e) {
      console.error('error', e);
      throw e;
    }
  },
  async fetchAllStaff({ commit }) {
    try {
      const staff = await staffService.fetchAllStaff();
      commit('setAllStaff', staff);
    } catch (e) {
      console.error('error', e);
    }
  },
  async toggleStaffCrmSettings({ rootGetters, commit }) {
    try {
      const staffUid = rootGetters['AuthStore/staffUid'];
      const staffSettings = await staffService.toggleStaffCrmSettings(staffUid);
      const staffCrmSettings = staffSettings.crm_settings;
      commit('setStaffData', { id: staffUid, new_crm_enabled: staffCrmSettings.new_crm_enabled, crm_settings_exists: staffCrmSettings.crm_settings_exists });
    } catch (e) {
      console.error('error', e);
    }
  },
  async setStaffTeamsView({ rootGetters, commit }, teamViewState) {
    try {
      const staffUid = rootGetters['AuthStore/staffUid'];
      commit('setStaffTeamsView', teamViewState);
      const data = teamViewState === 'all' ? '' : staffUid;
      outgoingPostMessageUtils.post('update-teams-view-state', data);
    } catch (e) {
      console.error('error', e);
    }
  },
  async setDefaultPage({ rootGetters, commit }, { defaultPage, skipTodoPage }) {
    try {
      const staffUid = rootGetters['AuthStore/staffUid'];
      const businessUid = rootGetters['AuthStore/businessUid'];
      // Update the local state immediately for a responsive UI
      commit('setStaffDefaultPage', { staffUid, defaultPage });
      // Call the APIs to save the preference
      await staffService.setDefaultPage(staffUid, defaultPage);
      await staffService.updateSkipTodoFlag(businessUid, skipTodoPage);
    } catch (e) {
      console.error('error', e);
    }
  },
  async fetchQuickActions({ commit }, showOnlyVisible = false) {
    try {
      const { data } = await staffService.fetchQuickActions(showOnlyVisible);
      commit('setQuickActions', getMappedQuickActions(data.staff_quick_action_lists[0].quick_actions));
    } catch (e) {
      console.error('error', e);
    }
  },
  async updateQuickActions({ commit, getters }, quickActions) {
    try {
      const { data } = await staffService.updateQuickActions(quickActions, getters.staffUid);
      commit('setQuickActions', getMappedQuickActions(data.staff_quick_action_lists[0].quick_actions));
    } catch (e) {
      console.error('error', e);
    }
  },
  async fetchStaffMemberData({ commit }, staffUid) {
    try {
      const staffData = await staffService.fetchStaffMemberData(staffUid);
      commit('setStaffMemberData', staffData);
    } catch (e) {
      console.error('error', e);
    }
  },
  async updateStaffMemberData({ commit }, { staffUid, staffData }) {
    const updatedData = await staffService.updateStaffMemberData(staffUid, staffData);
    if (updatedData && updatedData.uid) {
      commit('setStaffMemberData', updatedData);
    }

    return updatedData;
  },
};

const getters = {
  staffUid: (state, _, __, rootGetters) => rootGetters['AuthStore/staffUid'],
  staffData: (state, localGetters) => state.staffData[localGetters.staffUid] || {},
  staffMemberData: (state) => state.staffMemberData || {},
  staffDisplayName: (state, localGetters) => state.staffData[localGetters.staffUid].display_name,
  staffRole: (state, localGetters) => state.staffData[localGetters.staffUid].role,
  staffEmail: (state, localGetters) => state.staffData[localGetters.staffUid].email,
  staffTeamsViewEnabled: (state, localGetters) => state.staffData?.[localGetters.staffUid]?.teams_view_enabled ?? false,
  staffTeamsViewState: (state) => state.staffTeamsViewState,
  staffDataLoaded: (state) => state.staffDataLoaded,
  staffMemberDataLoaded: (state) => state.staffMemberDataLoaded,
  staffDataByUid: (state) => (uid) => state.staffData[uid],
  permissions: (state) => state.permissions,
  hasPermission: (state) => (permission) => state.permissions.includes(permission),
  allStaffData: (state) => state.staffData,
  allStaffLoaded: (state) => state.allStaffLoaded,
  isCollaborator: (state) => ['admin', 'manager', 'collaborator'].includes(state?.staffData[state.staffUid]?.role),
  staffList: (state) => state.staffList,
  staffOwner: (state) => state.staffList.find((staff) => staff.owner && !staff.deleted),
  defaultPage: (state, localGetters, _, rootGetters) => {
    const staffUid = localGetters.staffUid;
    let defaultPage;
    const defaultPageFromStore = state.staffData[staffUid]?.on_entry_navigate_to;
    if (defaultPageFromStore) {
      defaultPage = defaultPageFromStore;
    } else {
      const cachedDefaultPage = storageService.get(`${STORAGE_KEYS.defaultPage}_${staffUid}`);
      if (cachedDefaultPage) defaultPage = cachedDefaultPage;
    }
    // If the default page is 'pos' but the feature flag is disabled, reset to 'dashboard'
    if (defaultPage === 'pos' && !rootGetters['BusinessStore/hasFeature']('point_of_sale')) {
      return 'dashboard';
    }
    if (defaultPage) {
      return defaultPage;
    }
    if (localGetters.staffDataLoaded) {
      // If the default page is empty, and we already retrieved the defaultPage from the API, return a default value
      return 'dashboard';
    }
    return null;
  },
  quickActions: (state) => state.quickActions,
  visibleQuickActions: (state) => state.quickActions.filter((action) => action.visible).slice(0, MAX_VISIBLE_QUICK_ACTIONS),
};

export default {
  namespaced: true,
  modules: {
    staffPermissions: staffPermissionsStore,
    staffSlots: staffSlotsStore,
  },
  state,
  getters,
  actions,
  mutations,
};
